import React, { FC, useCallback, useEffect, useState } from 'react'
import { Add, Close, Done, EditOutlined, Warning } from '@mui/icons-material'
import { FormProvider, useForm } from 'react-hook-form'
import { ConfirmationModal, InputModal } from '../../common'
import Loader from '../../../common/Loader'
import { Button, IconButton } from '../../../ui'
import PermissionBlank from '../../../common/PermissionBlank'
import type { CurrentUserObject } from '../../../../lib/api/types/user.interfaces'
import { DocumentData } from '../../../../lib/api/types/document.interfaces'
import { ObjectPermissionSet } from '../../../../lib/api/types/permission.interfaces'
import cn from '../../../../lib/utils/cn'
import {
	useCreatePermissionSet,
	useUpdatePermissionSet,
} from '../../../../hooks/api/permissionSetHooks'
import { useSelectSet } from '../../../../hooks/api/permissionHooks'
import { getErrorData } from '../../../../lib/utils/handleApiError'
import PermissionsTable from './PermissionsTable'

export interface CurrentPermissionItem {
	level: number
	type: number
	id: string
	name: string
}

export interface CurrentPermissionBlank {
	level: number
	type: number
	id: string | null
	name: string
}

interface PermissionsContentProps {
	currentPermissions: CurrentPermissionItem[]
	currentUser: CurrentUserObject | null
	currentSet?: ObjectPermissionSet
	isLoading: boolean
	currentObject: DocumentData
	isEditable: boolean
	isAdmin: boolean
	onClose: () => void
}

const PermissionsContent: FC<PermissionsContentProps> = ({
	currentPermissions,
	currentUser,
	currentSet,
	currentObject,
	isLoading,
	isEditable,
	isAdmin,
	onClose,
}) => {
	const [isEditMode, setIsEditMode] = useState(false)
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
	const [isOpenInputModal, setIsOpenInputModal] = useState(false)

	const methods = useForm<{
		permissions: CurrentPermissionItem[]
		editingPermissions: CurrentPermissionItem[]
		currentBlank: CurrentPermissionBlank | null
	}>({
		defaultValues: {
			permissions: [],
			editingPermissions: [],
			currentBlank: null,
		},
	})

	const { watch, reset, setValue } = methods

	const { permissions, editingPermissions, currentBlank } = watch()

	const { mutateAsync: updateSet, isPending: isUpdatingSet } =
		useUpdatePermissionSet()
	const { mutateAsync: createSet, isPending: isCreatingSet } =
		useCreatePermissionSet()
	const { mutateAsync: applySet, isPending: isApplying } = useSelectSet()

	useEffect(() => {
		if (!isLoading && !!currentPermissions) {
			setValue('permissions', currentPermissions)
		}
	}, [currentPermissions, isLoading, setValue])

	const handleSave = async (confirmed?: boolean) => {
		if (!currentSet?.id) return

		const data = {
			name: currentSet?.name,
			permissions: permissions?.map(({ name, id, ...p }) => ({
				...p,
				toId: id,
			})),
			confirmed: Boolean(confirmed),
		}

		await updateSet(
			{ data, id: currentSet.id, invalidate: true },
			{
				onError: (error) => {
					const { errorMessageCode } = getErrorData(error)
					if (errorMessageCode === 'PERMISSION_SET_CONFIRMATION_REQUIRED') {
						setIsConfirmationModalOpen(true)
					}
				},
				onSuccess: onClose,
			}
		)
	}

	const handleCreateNewSet = async (newName?: string) => {
		if (!currentObject?.id) return
		const data = {
			name: newName ?? `${currentObject?.id}_ps`,
			permissions: permissions?.map(({ id, type, level, name }) => ({
				name,
				type: Number(type),
				level: Number(level),
				toId: id,
			})),
			isPersonal: !isAdmin,
		}

		const applySetCallback = async (setId: string) => {
			await applySet(
				{ setId, id: currentObject?.id},
				{
					onSuccess: () => {
						setIsOpenInputModal(false)
						onClose()
					},
				}
			)
		}

		await createSet(
			{ data, invalidate: true },
			{
				onSuccess: ({ data }) => {
					if (data?.data?.id) {
						applySetCallback(data?.data?.id)
					}
				},
				onError: (error) => {
					const { errorMessageCode } = getErrorData(error)
					if (errorMessageCode === 'PERMISSION_SET_SAME_NAME_EXISTS') {
						setIsOpenInputModal(true)
					}
				},
			}
		)
	}

	const handleCreateBlank = () => {
		if (currentBlank?.id) {
			setValue('editingPermissions', [
				...(editingPermissions || []),
				{
					id: currentBlank?.id,
					name: currentBlank?.name,
					level: Number(currentBlank?.level) || 0,
					type: currentBlank?.type,
				},
			])
		}
		setValue('currentBlank', {
			type: 0,
			level: 0,
			id: null,
			name: '',
		})
		if (!isEditMode) {
			setIsEditMode(true)
		}
	}

	const handleConfirmChanges = useCallback(() => {
		setIsEditMode(false)

		reset({
			permissions: currentBlank?.id
				? [
						...(editingPermissions ?? []),
						{
							id: currentBlank?.id,
							name: currentBlank?.name,
							level: currentBlank?.level,
							type: currentBlank?.type,
						},
				  ]
				: editingPermissions,
			editingPermissions: [],
			currentBlank: null,
		})
	}, [editingPermissions, currentBlank, reset])

	const displayedPermissions =
		(isEditMode ? editingPermissions : permissions) || []

	return (
		<div className="h-full">
			<div className="mb-5 default-shadow rounded-lg border border-stroke-light dark:border-stroke-light-dark  h-full">
				<div className="flex items-center justify-between p-4 pr-2 border-b border-stroke-light dark:border-stroke-light-dark ">
					<p className="font-semibold text-base text-text-main dark:text-text-table-dark">
						Permissions
					</p>
					{!isLoading && isEditable && (
						<div className="h-8">
							{isEditMode ? (
								<div className="flex items-center justify-end gap-2">
									<IconButton
										className="h-7 w-7"
										onClick={() => {
											setIsEditMode(false)
											setValue('editingPermissions', [])
										}}
										variant="secondary"
										icon={<Close className="fill-inherit h-5 w-5" />}
										tooltip="Cancel changes"
									/>
									<IconButton
										className="h-7 w-7"
										onClick={handleConfirmChanges}
										icon={<Done className="fill-white h-5 w-5" />}
										tooltip="Confirm changes"
									/>
								</div>
							) : (
								!!displayedPermissions?.length && (
									<Button
										label="Edit"
										onClick={() => {
											setValue('editingPermissions', permissions)
											setIsEditMode(true)
										}}
										variant="secondary"
										leftIcon={<EditOutlined className="fill-inherit h-5 w-5" />}
										tooltip="Edit set's permissions"
									/>
								)
							)}
						</div>
					)}
				</div>
				{!!displayedPermissions?.length && (
					<div className="flex items-center justify-start gap-2 border-b border-stroke-light dark:border-stroke-light-dark  bg-background-light dark:text-text-table-dark dark:bg-background-dark text-text-table text-xs font-semibold">
						<p className="h-10 w-20 md:w-36 pl-4 border-box content-center">
							Type
						</p>
						<p className="h-10 w-20  md:w-36 pl-4 border-box content-center">
							Name
						</p>
						<p className="h-10 w-20 md:w-36 pl-4 border-box content-center">
							Access
						</p>
						{isEditMode && <p className="h-10 w-11"></p>}
					</div>
				)}

				<div className="relative h-fit min-h-[48px] overflow-y-visible">
					{isLoading && !displayedPermissions ? (
						<Loader
							size={30}
							classNameWrapper="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2"
						/>
					) : (
						<FormProvider {...methods}>
							<PermissionsTable
								isLoading={isLoading}
								isEditMode={isEditMode}
								permissions={isEditMode ? editingPermissions : permissions}
								onCreateBlank={handleCreateBlank}
							/>
							{isEditMode && (
								<div className="w-full flex flex-col items-center h-fit">
									{!!currentBlank && (
										<PermissionBlank
											currentPermissions={[
												...permissions,
												...editingPermissions,
											]}
											className={cn(
												!displayedPermissions?.length && 'border-t-0'
											)}
										/>
									)}
									<div className="p-1.5 w-full box-border">
										<Button
											label="Add"
											variant="secondary"
											onClick={handleCreateBlank}
											disabled={!!currentBlank && !currentBlank?.id}
											className="w-full flex items-center justify-center"
										/>
									</div>
								</div>
							)}
						</FormProvider>
					)}
				</div>
			</div>
			<div className="flex-end flex gap-2">
				<Button
					variant="tertiary"
					className=""
					label="Reset"
					onClick={() => {
						reset({
							permissions: currentPermissions,
							editingPermissions: [],
						})
						setIsEditMode(false)
					}}
					disabled={isEditMode}
					tooltip={
						isEditMode
							? 'Unavailable in editing mode'
							: "Reset to set's configuration"
					}
				/>
				<Button
					variant="primary"
					className="w-16 flex items-center justify-center"
					label="Save"
					tooltip="Save changes"
					onClick={() => (currentSet?.id ? handleSave() : handleCreateNewSet())}
					disabled={
						isEditMode || isCreatingSet || isUpdatingSet || !permissions?.length
					}
					loading={Boolean(isCreatingSet || isUpdatingSet || isApplying)}
				/>
			</div>

			<ConfirmationModal
				isOpen={isConfirmationModalOpen}
				onClose={() => setIsConfirmationModalOpen(false)}
				onCancel={() => {
					setIsConfirmationModalOpen(false)
					setIsOpenInputModal(true)
				}}
				onAction={() => handleSave(true)}
				cancelLabel="Create new"
				cancelIcon={<Add />}
				actionLabel="Modify"
				icon={<Warning className="fill-accent" />}
				title="Confirmation"
				className="max-w-[200px]"
				description={
					isAdmin || currentSet?.userId === currentUser?.id ? (
						<p className="text-sm text-text-main dark:text-text-main-dark">
							This set <span className="font-bold">{currentSet?.name}</span> is
							applied to other objects. Modifying this set will change the
							access to other objects. <br />
							<br />
							Are you sure you want to modify?
						</p>
					) : (
						<p className="text-sm text-text-main dark:text-text-main-dark">
							Set &quot;{currentSet?.name}&quot; is admin set, you don&apos;t
							have permissions to modify this set. <br />
							<br />
							Do you want to create a new set?
						</p>
					)
				}
				loading={isConfirmationModalOpen && (isUpdatingSet || isCreatingSet)}
			/>
			<InputModal
				isOpen={isOpenInputModal}
				onClose={() => setIsOpenInputModal(false)}
				onAction={(newName) => handleCreateNewSet(newName)}
				icon={<Add />}
				inputPlaceholder="Name of set..."
				inputLabel="Name"
				title="Create new set"
				defaultValue={`${currentObject?.name ?? ''} custom set`}
				loading={isOpenInputModal && isCreatingSet}
			/>
		</div>
	)
}

export default PermissionsContent
